import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Grid,
  Divider,
  CircularProgress,
  Box,
  Card,
  CardContent,
  CardMedia,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  styled
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useAuth } from '../contexts/AuthContext';
import { formatLocationDisplay } from '../utils/formatting';
import { db, auth } from '../firebase';
import {
  doc,
  collection,
  addDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  getDoc
} from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  fetchSignInMethodsForEmail
} from 'firebase/auth';
import PaymentForm from '../components/subscriptions/PaymentForm';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(3),
}));

const EventDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1.5),
}));

const FormField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '1.5rem',
  marginTop: theme.spacing(1),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 0),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
}));

// Steps for the ticket purchase flow
const steps = ['Account Setup', 'Ticket Selection', 'Attendee Information', 'Payment & Confirmation'];

// Interface for route parameters
interface RouteParams {
  [key: string]: string | undefined;
  organizationId?: string;
  eventId?: string;
}

// Interface for ticket types
interface TicketType {
  id: string;
  name: string;
  price: number;
  description?: string;
  available: boolean;
}

// Interface for the event
interface Event {
  id: string;
  title: string;
  description: string;
  startDate: Date | { seconds: number; nanoseconds: number };
  endDate?: Date | { seconds: number; nanoseconds: number };
  location: string;
  imageUrl: string;
  price: number;
  ticketTypes: TicketType[];
  capacity: number;
  registeredCount: number;
  category: string;
}

// Interface for the organization
interface Organization {
  id: string;
  name: string;
  logo: string;
  description: string;
  verified: boolean;
}

// Interface for attendee information
interface Attendee {
  firstName: string;
  lastName: string;
  email: string;
}

const TicketPurchasePage: React.FC = () => {
  const params = useParams<RouteParams>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const organizationId = params.organizationId || '';
  const eventId = params.eventId || '';

  // State variables
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [event, setEvent] = useState<Event | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);

  // Form data
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedTicketType, setSelectedTicketType] = useState<string>('');
  const [ticketQuantity, setTicketQuantity] = useState<number>(1);
  const [attendees, setAttendees] = useState<Attendee[]>([{ firstName: '', lastName: '', email: '' }]);
  const [paymentMethod, setPaymentMethod] = useState<any>(null);

  // Format date
  const formatDate = (date: Date | { seconds: number; nanoseconds: number }) => {
    const dateObj = date instanceof Date ? date : new Date((date as any).seconds * 1000);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (date: Date | { seconds: number; nanoseconds: number }) => {
    const dateObj = date instanceof Date ? date : new Date((date as any).seconds * 1000);
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate total price
  const calculateTotal = () => {
    if (!event || !selectedTicketType) return 0;
    const ticket = event.ticketTypes.find(t => t.id === selectedTicketType);
    return ticket ? ticket.price * ticketQuantity : 0;
  };

  // Fetch event and organization details
  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);

        if (!organizationId || !eventId) {
          setError('Invalid event link');
          setLoading(false);
          return;
        }

        // In a real app, these would be Firestore queries
        // For demo purposes, we'll use hard-coded data

        // Mock event data
        const mockEvent: Event = {
          id: eventId,
          title: 'Summer Hiking Adventure',
          description: 'Join us for a day of hiking through the beautiful trails of Rocky Mountain National Park. This event is suitable for all experience levels.',
          startDate: new Date(2025, 5, 15, 9, 0), // June 15, 2025, 9:00 AM
          endDate: new Date(2025, 5, 15, 16, 0), // June 15, 2025, 4:00 PM
          location: 'Rocky Mountain National Park',
          imageUrl: 'https://picsum.photos/seed/event1/600/400',
          price: 25,
          ticketTypes: [
            {
              id: 'ticket1',
              name: 'Standard Pass',
              price: 25,
              description: 'Access to the guided hiking tour and lunch',
              available: true
            },
            {
              id: 'ticket2',
              name: 'Premium Pass',
              price: 40,
              description: 'Includes equipment rental, guided tour, and lunch',
              available: true
            }
          ],
          capacity: 50,
          registeredCount: 32,
          category: 'outdoor'
        };

        // Mock organization data
        const mockOrganization: Organization = {
          id: organizationId,
          name: 'Adventure Seekers',
          logo: 'https://picsum.photos/seed/org1/200/200',
          description: 'Adventure Seekers connects outdoor enthusiasts with thrilling experiences around the world.',
          verified: true
        };

        setEvent(mockEvent);
        setOrganization(mockOrganization);

        // Pre-select the first ticket type
        if (mockEvent.ticketTypes.length > 0) {
          setSelectedTicketType(mockEvent.ticketTypes[0].id);
        }

        // Pre-fill user info if logged in
        if (currentUser) {
          setEmail(currentUser.email || '');
          if (activeStep === 0) setActiveStep(1); // Skip account creation
        }

      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Failed to load event details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [organizationId, eventId, currentUser, activeStep]);

  // Handle next step
  const handleNext = async () => {
    if (activeStep === 0) {
      // Account creation step
      if (!email || !password || !firstName || !lastName) {
        setError('Please fill in all required fields');
        return;
      }

      try {
        setLoading(true);

        // Check if user already exists
        const methods = await fetchSignInMethodsForEmail(auth, email);

        if (methods.length > 0) {
          setError('An account with this email already exists. Please log in first.');
          setLoading(false);
          return;
        }

        // Create new user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // Update user profile
        if (userCredential.user) {
          await updateProfile(userCredential.user, {
            displayName: `${firstName} ${lastName}`
          });

          // Create user document in Firestore
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email,
            firstName,
            lastName,
            displayName: `${firstName} ${lastName}`,
            createdAt: serverTimestamp(),
            isOnboarded: false
          });
        }

        setLoading(false);
        setActiveStep(prevStep => prevStep + 1);

      } catch (err: any) {
        console.error('Error creating account:', err);
        setError(err.message || 'Failed to create account');
        setLoading(false);
      }
    } else if (activeStep === 1) {
      // Ticket selection step
      if (!selectedTicketType) {
        setError('Please select a ticket type');
        return;
      }

      if (ticketQuantity <= 0) {
        setError('Please select at least one ticket');
        return;
      }

      // Initialize attendees based on quantity
      const newAttendees = [...Array(ticketQuantity)].map((_, index) => {
        if (index === 0 && currentUser) {
          // Pre-fill first attendee with user info
          return {
            firstName: firstName || '',
            lastName: lastName || '',
            email: email || ''
          };
        }
        return { firstName: '', lastName: '', email: '' };
      });

      setAttendees(newAttendees);
      setActiveStep(prevStep => prevStep + 1);
    } else if (activeStep === 2) {
      // Attendee information step
      const isValid = attendees.every(attendee => 
        attendee.firstName.trim() !== '' && 
        attendee.lastName.trim() !== '' &&
        attendee.email.trim() !== ''
      );

      if (!isValid) {
        setError('Please fill in all attendee information');
        return;
      }

      setActiveStep(prevStep => prevStep + 1);
    } else if (activeStep === 3) {
      // Payment step
      if (!paymentMethod) {
        setError('Please add a valid payment method');
        return;
      }

      try {
        setLoading(true);

        // Process payment
        // In a real app, you would call a payment processor here

        // Create ticket record
        if (currentUser && event && organization) {
          const ticketData = {
            eventId: event.id,
            eventName: event.title,
            eventDate: event.startDate,
            eventLocation: event.location,
            eventImage: event.imageUrl,
            organizationId: organization.id,
            organizationName: organization.name,
            organizationLogo: organization.logo,
            ticketType: event.ticketTypes.find(t => t.id === selectedTicketType)?.name || 'Standard',
            ticketPrice: calculateTotal(),
            purchaseDate: serverTimestamp(),
            status: 'active',
            attendees: attendees.map(a => `${a.firstName} ${a.lastName}`),
            ticketCode: `${organization.id.substring(0, 4).toUpperCase()}-${event.id.substring(0, 4).toUpperCase()}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`
          };

          // Add ticket to user's tickets collection
          await addDoc(collection(db, 'users', currentUser.uid, 'tickets'), ticketData);

          // Navigate to tickets page with success message
          navigate('/my-tickets', { state: { success: true, message: 'Ticket purchased successfully!' } });
        } else {
          throw new Error('User not authenticated or event data missing');
        }
      } catch (err: any) {
        console.error('Error processing payment:', err);
        setError(err.message || 'Failed to process payment');
        setLoading(false);
      }
    }
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  // Handle ticket type change
  const handleTicketTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedTicketType(event.target.value as string);
  };

  // Handle ticket quantity change
  const handleQuantityChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = parseInt(event.target.value as string, 10);
    setTicketQuantity(value);
  };

  // Handle attendee information change
  const handleAttendeeChange = (index: number, field: keyof Attendee, value: string) => {
    const newAttendees = [...attendees];
    newAttendees[index] = { ...newAttendees[index], [field]: value };
    setAttendees(newAttendees);
  };

  // Render account setup step
  const renderAccountSetup = () => (
    <StyledPaper>
      <Typography variant="h6" gutterBottom>
        Create an Account
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Create an account to purchase tickets and manage your bookings.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormField
            label="First Name"
            fullWidth
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormField
            label="Last Name"
            fullWidth
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            label="Email"
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FormField
            label="Password"
            fullWidth
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Grid>
      </Grid>
    </StyledPaper>
  );

  // Render ticket selection step
  const renderTicketSelection = () => {
    if (!event) return null;

    return (
      <StyledPaper>
        <Typography variant="h6" gutterBottom>
          Select Tickets
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Choose your ticket type and quantity.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
              <InputLabel>Ticket Type</InputLabel>
              <Select
                value={selectedTicketType}
                onChange={handleTicketTypeChange as any}
                label="Ticket Type"
              >
                {event.ticketTypes.map((ticket) => (
                  <MenuItem key={ticket.id} value={ticket.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Typography>{ticket.name}</Typography>
                      <Typography variant="body2" color="primary" fontWeight="bold">
                        ${ticket.price.toFixed(2)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedTicketType && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  {event.ticketTypes.find(t => t.id === selectedTicketType)?.description}
                </Typography>
              </Box>
            )}

            <FormControl fullWidth variant="outlined">
              <InputLabel>Quantity</InputLabel>
              <Select
                value={ticketQuantity}
                onChange={handleQuantityChange as any}
                label="Quantity"
              >
                {[...Array(10)].map((_, i) => (
                  <MenuItem key={i + 1} value={i + 1}>
                    {i + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Order Summary
                </Typography>
                <StyledDivider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>
                    {event.ticketTypes.find(t => t.id === selectedTicketType)?.name || 'Ticket'} x {ticketQuantity}
                  </Typography>
                  <Typography>
                    ${calculateTotal().toFixed(2)}
                  </Typography>
                </Box>
                <StyledDivider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontWeight: 'bold' }}>
                  <Typography>Total</Typography>
                  <Typography>${calculateTotal().toFixed(2)}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </StyledPaper>
    );
  };

  // Render attendee information step
  const renderAttendeeInformation = () => (
    <StyledPaper>
      <Typography variant="h6" gutterBottom>
        Attendee Information
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Please provide information for each attendee.
      </Typography>

      {attendees.map((attendee, index) => (
        <Box key={index} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip label={index + 1} color="primary" size="small" sx={{ mr: 1 }} />
            Attendee {index + 1}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormField
                label="First Name"
                fullWidth
                value={attendee.firstName}
                onChange={(e) => handleAttendeeChange(index, 'firstName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormField
                label="Last Name"
                fullWidth
                value={attendee.lastName}
                onChange={(e) => handleAttendeeChange(index, 'lastName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormField
                label="Email"
                fullWidth
                type="email"
                value={attendee.email}
                onChange={(e) => handleAttendeeChange(index, 'email', e.target.value)}
                required
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </StyledPaper>
  );

  // Render payment and confirmation step
  const renderPaymentConfirmation = () => (
    <StyledPaper>
      <Typography variant="h6" gutterBottom>
        Payment & Confirmation
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Review your order and complete payment.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle1" gutterBottom>
            Payment Method
          </Typography>
          <PaymentForm onPaymentMethodSelected={setPaymentMethod} />
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Order Summary
              </Typography>
              <StyledDivider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>
                  {event?.ticketTypes.find(t => t.id === selectedTicketType)?.name || 'Ticket'} x {ticketQuantity}
                </Typography>
                <Typography>
                  ${calculateTotal().toFixed(2)}
                </Typography>
              </Box>
              <StyledDivider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, fontWeight: 'bold' }}>
                <Typography>Total</Typography>
                <Typography>${calculateTotal().toFixed(2)}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </StyledPaper>
  );

  // Render appropriate step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderAccountSetup();
      case 1:
        return renderTicketSelection();
      case 2:
        return renderAttendeeInformation();
      case 3:
        return renderPaymentConfirmation();
      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ my: 4 }}>
      {loading ? (
        <LoadingContainer>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading...</Typography>
        </LoadingContainer>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <>
          {event && organization && (
            <>
              {/* Event Information */}
              <Card sx={{ mb: 4 }}>
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <CardMedia
                      component="img"
                      height="100%"
                      image={event.imageUrl}
                      alt={event.title}
                      sx={{ minHeight: 200 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Box
                          component="img"
                          src={organization.logo}
                          alt={organization.name}
                          sx={{ width: 40, height: 40, mr: 1, borderRadius: '50%' }}
                        />
                        <Typography variant="subtitle1" color="text.secondary">
                          {organization.name}
                          {organization.verified && (
                            <VerifiedIcon
                              color="primary"
                              fontSize="small"
                              sx={{ ml: 0.5 }}
                              titleAccess="Verified Organization"
                            />
                          )}
                        </Typography>
                      </Box>

                      <Typography variant="h5" component="h1" gutterBottom>
                        {event.title}
                      </Typography>

                      <EventDetail>
                        <EventIcon sx={{ mt: 0.5, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {formatDate(event.startDate)}
                        </Typography>
                      </EventDetail>

                      <EventDetail>
                        <AccessTimeIcon sx={{ mt: 0.5, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {formatTime(event.startDate)}
                          {event.endDate && ` - ${formatTime(event.endDate)}`}
                        </Typography>
                      </EventDetail>

                      <EventDetail>
                        <LocationOnIcon sx={{ mt: 0.5, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {formatLocationDisplay(event.location)}
                        </Typography>
                      </EventDetail>

                      <EventDetail>
                        <PeopleIcon sx={{ mt: 0.5, mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">
                          {event.registeredCount} / {event.capacity} registered
                        </Typography>
                      </EventDetail>
                    </CardContent>
                  </Grid>
                </Grid>
              </Card>

              {/* Stepper */}
              <StyledStepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </StyledStepper>

              {/* Step Content */}
              {getStepContent(activeStep)}

              {/* Navigation Buttons */}
              <ButtonContainer>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                >
                  {activeStep === steps.length - 1 ? 'Complete Purchase' : 'Next'}
                </Button>
              </ButtonContainer>
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default TicketPurchasePage;
